import { useEffect, useRef } from "preact/hooks";

import {
  CalendarChipButton,
  occurrenceKey,
  resolveChipMeta,
} from "../components/CalendarChipButton";
import { isSameDay, toISODate, weekdayNarrowLabel } from "../lib/calendarDates";
import type { ScheduleOccurrence } from "../types";

export interface CalendarPhoneAgendaProps {
  weekDays: Date[];
  selectedDate: Date;
  onSelectDate: (date: Date) => void;
  occurrences: ScheduleOccurrence[];
  assignmentDateById: Map<string, string>;
  movedFromByAssignmentId: Map<string, boolean>;
  thisWeekDays: Date[];
  canWrite: boolean;
  onTapOccurrence: (occurrence: ScheduleOccurrence) => void;
  onAddForDay: (dateIso: string) => void;
  onPrevWeek: () => void;
  onNextWeek: () => void;
}

function dayHasDueOrOverdue(dateIso: string, occurrences: ScheduleOccurrence[]): boolean {
  return occurrences.some(
    (o) => o.date === dateIso && !o.exception && (o.status === "due" || o.status === "overdue"),
  );
}

/**
 * §2.4/§2.12 — phone's week strip + agenda (no month grid on phone at
 * all). The strip shows the current week only, with `‹`/`›` arrows on
 * either end (the same `.calendar-nav` buttons desktop uses) to step a
 * week at a time. `aria-current="date"` on the selected day (the WAI-ARIA
 * token for a selected day in a date collection — not `aria-selected`,
 * which implies a role this strip doesn't have, per m-B0b-5).
 */
export function CalendarPhoneAgenda({
  weekDays,
  selectedDate,
  onSelectDate,
  occurrences,
  assignmentDateById,
  movedFromByAssignmentId,
  thisWeekDays,
  canWrite,
  onTapOccurrence,
  onAddForDay,
  onPrevWeek,
  onNextWeek,
}: CalendarPhoneAgendaProps) {
  const selectedIso = toISODate(selectedDate);
  const agendaOccurrences = occurrences.filter((o) => o.date === selectedIso);
  const stripRef = useRef<HTMLDivElement>(null);

  // UX review (done-feedback) — the strip scrolls sideways on narrow
  // phones (each day is >=44px wide), so keep the selected day in view.
  // Sets the strip's own `scrollLeft` (never `scrollIntoView`, which can
  // also scroll the page vertically).
  useEffect(() => {
    const strip = stripRef.current;
    if (!strip || strip.scrollWidth <= strip.clientWidth) return;
    const selected = strip.querySelector<HTMLElement>('button[aria-current="date"]');
    if (!selected) return;
    strip.scrollLeft = selected.offsetLeft - (strip.clientWidth - selected.offsetWidth) / 2;
  }, [selectedIso]);

  return (
    <>
      <div class="calendar-day-strip-row">
        <button
          type="button"
          class="calendar-nav-btn"
          aria-label="Previous week"
          onClick={onPrevWeek}
        >
          ‹
        </button>
        <div class="calendar-day-strip" ref={stripRef}>
          {weekDays.map((date) => {
            const iso = toISODate(date);
            const isSelected = isSameDay(date, selectedDate);
            return (
              <button
                key={iso}
                type="button"
                aria-current={isSelected ? "date" : undefined}
                onClick={() => onSelectDate(date)}
              >
                <span>{weekdayNarrowLabel(date)}</span>
                <span class="calendar-day-strip-num">{date.getDate()}</span>
                {dayHasDueOrOverdue(iso, occurrences) ? (
                  <span class="calendar-day-strip-dot" aria-hidden="true" />
                ) : null}
              </button>
            );
          })}
        </div>
        <button type="button" class="calendar-nav-btn" aria-label="Next week" onClick={onNextWeek}>
          ›
        </button>
      </div>

      <ul class="calendar-agenda">
        {agendaOccurrences.map((occ) => {
          const meta = resolveChipMeta(
            occ,
            assignmentDateById,
            movedFromByAssignmentId,
            thisWeekDays,
          );
          return (
            <li key={occurrenceKey(occ)}>
              <CalendarChipButton
                occurrence={occ}
                moved={meta.moved}
                movedTargetLabel={meta.movedTargetLabel}
                onTap={onTapOccurrence}
              />
            </li>
          );
        })}
      </ul>
      {agendaOccurrences.length === 0 ? <p class="empty-hint">Nothing for this day.</p> : null}

      {canWrite ? (
        <button
          type="button"
          class="admin-row calendar-agenda-add-row"
          onClick={() => onAddForDay(selectedIso)}
        >
          <span class="admin-row-main">
            <span class="admin-row-name">+ Add for this day</span>
          </span>
        </button>
      ) : null}
    </>
  );
}
